import { useCallback, useEffect, useRef, useState } from "react";

const MIN_THUMB = 24;
// Content longer than the viewport by less than this doesn't scroll
const OVERFLOW_TOLERANCE = 1;
const HIDDEN_THUMB = { visible: false, height: 0, top: 0 };
const HIDDEN_THUMB_H = { visible: false, width: 0, left: 0 };

// Scroll axis names of the viewport and the pointer for each scrollbar
const AXES = {
  y: {
    scroll: "scrollTop",
    size: "clientHeight",
    total: "scrollHeight",
    pointer: "clientY",
    start: "top",
  },
  x: {
    scroll: "scrollLeft",
    size: "clientWidth",
    total: "scrollWidth",
    pointer: "clientX",
    start: "left",
  },
};

/**
 * Thumb size and offset along the track for one axis, null when the content
 * fits and there is nothing to scroll.
 */
const getThumb = (viewport, track, axis) => {
  const { scroll, size, total } = AXES[axis];
  const trackSize = track[size];
  const viewportSize = viewport[size];
  const contentSize = viewport[total];

  if (contentSize <= viewportSize + OVERFLOW_TOLERANCE || trackSize <= 0) {
    return null;
  }

  const thumbSize = Math.max(
    (viewportSize / contentSize) * trackSize,
    MIN_THUMB,
  );
  const maxScroll = contentSize - viewportSize;
  const maxOffset = Math.max(trackSize - thumbSize, 0);
  const offset = maxScroll > 0 ? (viewport[scroll] / maxScroll) * maxOffset : 0;

  return { size: thumbSize, offset };
};

export const useOverlayScrollbar = () => {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const trackHRef = useRef(null);
  const contentRef = useRef(null);
  const stopDragRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [thumb, setThumb] = useState(HIDDEN_THUMB);
  const [thumbH, setThumbH] = useState(HIDDEN_THUMB_H);

  const updateThumb = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    if (trackRef.current) {
      const next = getThumb(viewport, trackRef.current, "y");

      setThumb(
        next
          ? { visible: true, height: next.size, top: next.offset }
          : HIDDEN_THUMB,
      );
    }

    if (trackHRef.current) {
      const next = getThumb(viewport, trackHRef.current, "x");

      setThumbH(
        next
          ? { visible: true, width: next.size, left: next.offset }
          : HIDDEN_THUMB_H,
      );
    }
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport) return undefined;

    updateThumb();
    viewport.addEventListener("scroll", updateThumb, { passive: true });
    window.addEventListener("resize", updateThumb);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateThumb)
        : null;
    resizeObserver?.observe(viewport);
    if (content) resizeObserver?.observe(content);

    return () => {
      viewport.removeEventListener("scroll", updateThumb);
      window.removeEventListener("resize", updateThumb);
      resizeObserver?.disconnect();
    };
  }, [updateThumb]);

  // Drop window listeners if unmounted mid-drag
  useEffect(() => () => stopDragRef.current?.(), []);

  /**
   * Thumb drag and track click for one axis, the track scrolls the viewport
   * like a native scrollbar.
   */
  const startDrag = (axis) => (event) => {
    const viewport = viewportRef.current;
    const track = axis === "y" ? trackRef.current : trackHRef.current;
    if (!viewport || !track || event.button !== 0) return;

    event.preventDefault();

    const { scroll, size, total, pointer, start } = AXES[axis];
    const thumbSize = axis === "y" ? thumb.height : thumbH.width;
    const maxScroll = viewport[total] - viewport[size];
    const ratio = maxScroll / Math.max(track[size] - thumbSize, 1);

    // A click on the track outside the thumb jumps there first
    if (event.target === track) {
      const offset = event[pointer] - track.getBoundingClientRect()[start];
      viewport[scroll] = (offset - thumbSize / 2) * ratio;
    }

    const startPointer = event[pointer];
    const startScroll = viewport[scroll];

    const handleMove = (moveEvent) => {
      viewport[scroll] =
        startScroll + (moveEvent[pointer] - startPointer) * ratio;
    };

    const stopDrag = () => {
      stopDragRef.current = null;
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };

    const handleUp = () => {
      stopDrag();
      setDragging(false);
    };

    stopDragRef.current = stopDrag;
    setDragging(true);
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
  };

  // The scrollbar stays visible while dragged outside the container
  const isActive = hovered || dragging;

  return {
    viewportRef,
    trackRef,
    trackHRef,
    contentRef,
    thumb,
    thumbH,
    showTrack: isActive && thumb.visible,
    showTrackH: isActive && thumbH.visible,
    dragging,
    onTrackPointerDown: startDrag("y"),
    onTrackHPointerDown: startDrag("x"),
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
  };
};
